<script setup>
/**
 * PricingSection v3 — "everything included, custom quote".
 *
 * Past version had three tier-cards (Starter / Growth / Enterprise)
 * that implied feature-gating between plans. That was wrong: every
 * customer gets every capability, only volume drives price. This
 * version drops the tiers and instead lists the platform's actual
 * capabilities in a 6-up grid of category cards, so the visitor sees
 * the depth of what they're paying for before asking for a quote.
 *
 * Shape:
 *  - Eyebrow + title + subtitle (same)
 *  - 6 category cards (Tracking, Analytics, Alerts, Privacy,
 *    Deployment, Customization & Integrations). Static grid, no
 *    rotation — this is dense info, not entertainment.
 *  - Commercial reasons trio (seats / no-lockin / advisory) kept as
 *    a small horizontal strip below the inclusions.
 *  - ROI calculator preserved as the rational-buyer anchor.
 *  - Final CTA to the contact form.
 *
 * Tier i18n keys (landing.pricing.tiers.* + recommended_label +
 * tier_price_*) have been removed in this commit; recover from git
 * if we ever want them back.
 */
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Check, ArrowRight, Calculator,
  Activity, BarChart3, Bell, ShieldCheck, Package, Settings2,
  Users2, BadgeCheck, Headphones,
} from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t, tm } = useI18n();

// Read an i18n list reliably (array OR object-with-numeric-keys both
// happen in practice depending on how the JSON is hand-authored).
function listFromI18n(path) {
  const v = tm(path);
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") return Object.values(v);
  return [];
}

// 6 inclusion categories — these mirror the i18n keys under
// landing.pricing.inclusions.*. Each carries an accent colour driving
// the top stripe, the icon stamp and the check marks.
const inclusions = computed(() => [
  {
    key: "tracking",
    icon: Activity,
    accent: "cyan",
    name: t("landing.pricing.inclusions.tracking.name"),
    kicker: t("landing.pricing.inclusions.tracking.kicker"),
    features: listFromI18n("landing.pricing.inclusions.tracking.features"),
  },
  {
    key: "analytics",
    icon: BarChart3,
    accent: "violet",
    name: t("landing.pricing.inclusions.analytics.name"),
    kicker: t("landing.pricing.inclusions.analytics.kicker"),
    features: listFromI18n("landing.pricing.inclusions.analytics.features"),
  },
  {
    key: "alerts",
    icon: Bell,
    accent: "amber",
    name: t("landing.pricing.inclusions.alerts.name"),
    kicker: t("landing.pricing.inclusions.alerts.kicker"),
    features: listFromI18n("landing.pricing.inclusions.alerts.features"),
  },
  {
    key: "privacy",
    icon: ShieldCheck,
    accent: "emerald",
    name: t("landing.pricing.inclusions.privacy.name"),
    kicker: t("landing.pricing.inclusions.privacy.kicker"),
    features: listFromI18n("landing.pricing.inclusions.privacy.features"),
  },
  {
    key: "deployment",
    icon: Package,
    accent: "orange",
    name: t("landing.pricing.inclusions.deployment.name"),
    kicker: t("landing.pricing.inclusions.deployment.kicker"),
    features: listFromI18n("landing.pricing.inclusions.deployment.features"),
  },
  {
    key: "custom",
    icon: Settings2,
    accent: "rose",
    name: t("landing.pricing.inclusions.custom.name"),
    kicker: t("landing.pricing.inclusions.custom.kicker"),
    features: listFromI18n("landing.pricing.inclusions.custom.features"),
  },
]);

// Per-accent style packs (same family as Alerts/UseCases sections so
// the visual language stays consistent). `emerald` added here — the
// other sections didn't need it. Tailwind JIT picks up class strings.
const ACCENT = {
  cyan:    { text: "text-cyan-600 dark:text-cyan-400",     stripe: "bg-cyan-500/85",    iconHsl: "190 90% 45%" },
  violet:  { text: "text-violet-600 dark:text-violet-400", stripe: "bg-violet-500/85",  iconHsl: "262 70% 58%" },
  amber:   { text: "text-amber-600 dark:text-amber-400",   stripe: "bg-amber-500/85",   iconHsl: "38 92% 50%"  },
  emerald: { text: "text-emerald-600 dark:text-emerald-400", stripe: "bg-emerald-500/85", iconHsl: "142 71% 45%" },
  orange:  { text: "text-orange-600 dark:text-orange-400", stripe: "bg-orange-500/85",  iconHsl: "24 90% 55%"  },
  rose:    { text: "text-rose-600 dark:text-rose-400",     stripe: "bg-rose-500/85",    iconHsl: "350 80% 58%" },
};

const iconStampStyle = (accent) => {
  const hsl = ACCENT[accent].iconHsl;
  return {
    background: `linear-gradient(135deg, hsl(${hsl} / 0.28) 0%, hsl(${hsl} / 0.12) 100%)`,
    border: `1px solid hsl(${hsl} / 0.42)`,
    boxShadow: `inset 0 1px 0 hsl(0 0% 100% / 0.35), 0 0 18px hsl(${hsl} / 0.20), 0 1px 2px hsl(${hsl} / 0.15)`,
    color: `hsl(${hsl})`,
  };
};

// Commercial-terms trio (existing i18n keys, kept as-is).
const reasons = computed(() => [
  { icon: Users2,     title: t("landing.pricing.reasons.seats.title"),     description: t("landing.pricing.reasons.seats.description") },
  { icon: BadgeCheck, title: t("landing.pricing.reasons.no_lockin.title"), description: t("landing.pricing.reasons.no_lockin.description") },
  { icon: Headphones, title: t("landing.pricing.reasons.advisory.title"),  description: t("landing.pricing.reasons.advisory.description") },
]);

// === ROI Calculator state ===
const roi = ref({
  employees: 50,
  hourlyCost: 25,
  improvementPct: 12,
});

const annualSavings = computed(() => {
  const hoursPerYear = 1760;
  const value =
    roi.value.employees *
    roi.value.hourlyCost *
    hoursPerYear *
    (roi.value.improvementPct / 100);
  return Math.max(0, Math.round(value));
});

const formatEUR = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
</script>

<template>
  <section id="pricing" class="py-24 bg-background scroll-mt-20">
    <div class="container mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-14 max-w-2xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
          {{ t("landing.pricing.eyebrow") }}
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
          {{ t("landing.pricing.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.pricing.subtitle") }}
        </p>
      </AnimatedSection>

      <!-- 6 inclusion cards. Static grid (no rotation): this is a
           dense info inventory, not an animated showcase. Card style
           mirrors the carousel sections so the visual family is
           consistent. -->
      <div class="max-w-6xl mx-auto mb-16">
        <p class="text-xs font-semibold uppercase tracking-widest text-accent text-center mb-6">
          {{ t("landing.pricing.inclusions_eyebrow") }}
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatedSection
            v-for="(inc, idx) in inclusions"
            :key="inc.key"
            animation="fade-up"
            :delay="idx * 70"
          >
            <article class="relative h-full rounded-xl card-elevated-premium overflow-hidden flex flex-col">
              <!-- Top stripe in category accent -->
              <div class="h-[3px] w-full" :class="ACCENT[inc.accent].stripe" />

              <div class="p-5 lg:p-6 flex flex-col flex-1">
                <!-- Header row: icon stamp + ordinal -->
                <div class="flex items-start justify-between mb-4">
                  <div
                    class="inline-flex items-center justify-center w-11 h-11 rounded-xl"
                    :style="iconStampStyle(inc.accent)"
                  >
                    <component :is="inc.icon" class="w-5 h-5" />
                  </div>
                  <span class="text-[10px] font-mono uppercase tracking-wider text-muted-foreground tabular-nums pt-1">
                    {{ String(idx + 1).padStart(2, "0") }} / 06
                  </span>
                </div>

                <h3 class="text-base lg:text-lg font-bold text-foreground mb-1 tracking-tight leading-snug">
                  {{ inc.name }}
                </h3>

                <p class="text-xs italic text-muted-foreground/75 mb-4 leading-snug">
                  {{ inc.kicker }}
                </p>

                <!-- Feature list — checks tinted in category accent so
                     the eye can still parse each tile's identity. -->
                <ul class="space-y-2 mt-auto">
                  <li
                    v-for="(f, i) in inc.features"
                    :key="i"
                    class="flex items-start gap-2 text-[13px] leading-relaxed"
                  >
                    <Check
                      :class="['w-3.5 h-3.5 mt-1 flex-shrink-0', ACCENT[inc.accent].text]"
                    />
                    <span class="text-foreground/85">{{ f }}</span>
                  </li>
                </ul>
              </div>
            </article>
          </AnimatedSection>
        </div>
      </div>

      <!-- Commercial reasons trio — small horizontal strip with the
           three commitments that frame the pricing model itself
           (pay-by-active-seats, no lock-in, advisory included). -->
      <AnimatedSection animation="fade-up" :delay="100">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-16">
          <div
            v-for="(r, idx) in reasons"
            :key="idx"
            class="flex items-start gap-3 p-4 rounded-lg border border-border bg-card/50"
          >
            <component :is="r.icon" class="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-foreground mb-1 leading-tight">
                {{ r.title }}
              </p>
              <p class="text-xs text-muted-foreground leading-relaxed">
                {{ r.description }}
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <!-- ROI Calculator: rational buyer's anchor. Three inputs → one
           big number. Pure client-side. Kept from the previous version
           because it earns its place — a CFO-ready savings estimate
           bridges "no tiered prices" with "give me a number". -->
      <AnimatedSection animation="scale" :delay="200">
        <div class="max-w-5xl mx-auto rounded-2xl border border-border bg-card surface-elevated overflow-hidden">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-0">
            <div class="lg:col-span-7 p-7 lg:p-9 border-b lg:border-b-0 lg:border-r border-border">
              <div class="flex items-center gap-2 mb-1.5">
                <Calculator class="w-4 h-4 text-accent" />
                <span class="text-xs font-semibold uppercase tracking-wider text-accent">
                  {{ t("landing.pricing.calculator.eyebrow") }}
                </span>
              </div>
              <h3 class="text-2xl font-bold text-foreground mb-2 tracking-tight">
                {{ t("landing.pricing.calculator.title") }}
              </h3>
              <p class="text-sm text-muted-foreground mb-6">
                {{ t("landing.pricing.calculator.subtitle") }}
              </p>

              <div class="space-y-5">
                <div>
                  <Label class="text-foreground mb-2 block text-xs font-semibold uppercase tracking-wider">
                    {{ t("landing.pricing.calculator.employees") }}
                  </Label>
                  <div class="flex items-center gap-3">
                    <input
                      type="range"
                      min="5"
                      max="500"
                      step="5"
                      v-model.number="roi.employees"
                      class="flex-1 accent-accent"
                    />
                    <Input
                      type="number"
                      min="1"
                      max="5000"
                      v-model.number="roi.employees"
                      class="w-24 text-center font-semibold"
                    />
                  </div>
                </div>

                <div>
                  <Label class="text-foreground mb-2 block text-xs font-semibold uppercase tracking-wider">
                    {{ t("landing.pricing.calculator.hourly_cost") }}
                  </Label>
                  <div class="flex items-center gap-3">
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="1"
                      v-model.number="roi.hourlyCost"
                      class="flex-1 accent-accent"
                    />
                    <div class="relative w-24">
                      <Input
                        type="number"
                        min="1"
                        max="500"
                        v-model.number="roi.hourlyCost"
                        class="text-center font-semibold pr-7"
                      />
                      <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label class="text-foreground mb-2 block text-xs font-semibold uppercase tracking-wider">
                    {{ t("landing.pricing.calculator.improvement") }}
                  </Label>
                  <div class="flex items-center gap-3">
                    <input
                      type="range"
                      min="3"
                      max="30"
                      step="1"
                      v-model.number="roi.improvementPct"
                      class="flex-1 accent-accent"
                    />
                    <div class="relative w-24">
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        v-model.number="roi.improvementPct"
                        class="text-center font-semibold pr-7"
                      />
                      <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="lg:col-span-5 p-7 lg:p-9 bg-inverse text-inverse-foreground flex flex-col justify-center">
              <p class="text-xs font-semibold uppercase tracking-widest text-inverse-accent mb-3">
                {{ t("landing.pricing.calculator.output_label") }}
              </p>
              <p class="text-4xl lg:text-5xl xl:text-6xl font-bold tabular-nums tracking-tight mb-3 leading-[1.05]">
                {{ formatEUR(annualSavings) }}
              </p>
              <p class="text-sm text-inverse-muted-foreground mb-6 leading-relaxed">
                {{ t("landing.pricing.calculator.output_caption") }}
              </p>
              <a href="#contact-form" class="inline-block">
                <Button variant="hero-primary" size="default" class="w-full sm:w-auto">
                  {{ t("landing.pricing.calculator.cta") }}
                  <ArrowRight class="w-3.5 h-3.5" />
                </Button>
              </a>
              <p class="text-[10px] text-inverse-muted-foreground mt-4">
                {{ t("landing.pricing.calculator.disclaimer") }}
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  </section>
</template>
